import { scaleLinear } from 'd3-scale'

import { m } from '#lib/paraglide/messages.js'

// Axis tick labels render as `text-xs` (see ui/chart/chart-container.svelte)
const TICK_FONT_SIZE = 12
// Tick mark length and the distance from the axis line to its tick labels, like unovis axes
const TICK_LENGTH = 6
const TICK_LABEL_OFFSET = 10
// Gap between the widest y tick label and the chart edge
const TICK_LABEL_GAP = 6

export const X_AXIS_PROPS = {
	rule: true,
	tickLength: TICK_LENGTH,
	tickLabelProps: { dy: TICK_LABEL_OFFSET },
}

export const Y_AXIS_PROPS = {
	rule: true,
	grid: true,
	tickLength: TICK_LENGTH,
	tickLabelProps: { dx: -TICK_LABEL_OFFSET },
}

let canvasContext: CanvasRenderingContext2D | null | undefined

const measureText = (text: string) => {
	if (
		typeof document === 'undefined' ||
		!(canvasContext ??= document.createElement('canvas').getContext('2d'))
	) {
		// SSR or no canvas: rough average glyph width
		return text.length * TICK_FONT_SIZE * 0.6
	}

	canvasContext.font = `${TICK_FONT_SIZE}px ${getComputedStyle(document.body).fontFamily}`

	return canvasContext.measureText(text).width
}

/**
 * Chart padding beside the value axis that fits its widest tick label.
 * Layerchart uses a fixed padding, so long labels (ex. `$0.00025`, `0.0002 ALGO`) get clipped.
 */
export const getValueAxisPadding = (
	yDomain: [number, number],
	format: (value: number) => string
) => {
	const scale = scaleLinear().domain(yDomain)
	// Tick count depends on chart height, so check the likely range of counts
	const ticks = [5, 6, 8, 10].flatMap(count => scale.ticks(count))
	const width = Math.max(...ticks.map(tick => measureText(format(tick))), 0)

	return Math.ceil(width) + TICK_LABEL_OFFSET + TICK_LABEL_GAP
}

// Keep bars at most BAR_MAX_WIDTH wide when only a few are shown, like unovis `barMaxWidth`
const BAR_MAX_WIDTH = 150

export const getBandPadding = (count: number, length: number | undefined) =>
	length && count > 0 ? Math.min(0.9, Math.max(0.1, 1 - (BAR_MAX_WIDTH * count) / length)) : 0.1

/**
 * Size of the icons next to the bars: ~60% of each band, between 20px and 36px.
 * Takes the container length, not the chart's: the chart's depends on padding,
 * which depends on this size.
 */
export const getBarIconSize = (count: number, containerLength: number | undefined) =>
	containerLength && count > 0
		? Math.round(Math.min(36, Math.max(20, (containerLength / count) * 0.6)))
		: 28

/**
 * Whether value labels fit by their bars: wide enough above vertical bars,
 * tall enough beside horizontal ones. Hidden otherwise, as they would overlap.
 */
export const canShowBarLabels = (labels: string[], bandLength: number, horizontal: boolean) =>
	horizontal
		? bandLength >= TICK_FONT_SIZE + 2
		: Math.max(0, ...labels.map(measureText)) + 4 <= bandLength

export const getIconAxisPadding = (iconSize: number) => TICK_LABEL_OFFSET + iconSize + 6

export const X_AXIS_LABELS_HEIGHT = TICK_LABEL_OFFSET + TICK_FONT_SIZE + 8

// Metric menu groups shared by the bar and line charts
export const METRIC_CATEGORIES = {
	scalability: () => m.menu_dashboard_scalability(),
	decentralization: () => m.menu_dashboard_decentralization(),
	'dev-activity': () => m.menu_dashboard_dev_activity(),
	financials: () => m.menu_dashboard_financials(),
}
